import React from 'react';

interface CustomCandlesLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const CustomCandlesLogo: React.FC<CustomCandlesLogoProps> = ({ className = '', size = 'md' }) => {
  const sizes = {
    sm: { width: 60, height: 40, candleWidth: 8, candleHeight: 16, flameSize: 6 },
    md: { width: 80, height: 50, candleWidth: 10, candleHeight: 20, flameSize: 8 },
    lg: { width: 100, height: 60, candleWidth: 12, candleHeight: 24, flameSize: 10 }
  };

  const currentSize = sizes[size];

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* Decorative candles with artistic elements */}
      <svg
        width={currentSize.width}
        height={currentSize.height * 0.7}
        viewBox={`0 0 ${currentSize.width} ${currentSize.height * 0.7}`}
        className="mb-2"
      >
        {/* Decorative background circle */}
        <circle
          cx={currentSize.width / 2}
          cy={currentSize.height * 0.35}
          r={currentSize.width * 0.4}
          fill="#FEF3C7"
          stroke="#F59E0B"
          strokeWidth="0.5"
          opacity="0.3"
        />
        
        {/* Left Artistic Candle */}
        <g>
          {/* Candle body with decorative pattern */}
          <rect
            x={currentSize.width * 0.2}
            y={currentSize.height * 0.25}
            width={currentSize.candleWidth}
            height={currentSize.candleHeight}
            fill="#D97706"
            stroke="#92400E"
            strokeWidth="0.3"
            rx="1"
          />
          {/* Decorative swirl on candle */}
          <path
            d={`M ${currentSize.width * 0.22} ${currentSize.height * 0.35} Q ${currentSize.width * 0.25} ${currentSize.height * 0.3} ${currentSize.width * 0.28} ${currentSize.height * 0.35} Q ${currentSize.width * 0.25} ${currentSize.height * 0.4} ${currentSize.width * 0.22} ${currentSize.height * 0.35}`}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="0.4"
          />
          {/* Wick */}
          <line
            x1={currentSize.width * 0.2 + currentSize.candleWidth / 2}
            y1={currentSize.height * 0.25}
            x2={currentSize.width * 0.2 + currentSize.candleWidth / 2}
            y2={currentSize.height * 0.2}
            stroke="#92400E"
            strokeWidth="0.5"
          />
          {/* Artistic flame with sparkles */}
          <ellipse
            cx={currentSize.width * 0.2 + currentSize.candleWidth / 2}
            cy={currentSize.height * 0.15}
            rx={currentSize.flameSize / 2}
            ry={currentSize.flameSize / 1.5}
            fill="#FCD34D"
            opacity="0.9"
          />
          <ellipse
            cx={currentSize.width * 0.2 + currentSize.candleWidth / 2}
            cy={currentSize.height * 0.15}
            rx={currentSize.flameSize / 4}
            ry={currentSize.flameSize / 2.5}
            fill="#F59E0B"
          />
          {/* Sparkle effect */}
          <circle
            cx={currentSize.width * 0.2 + currentSize.candleWidth / 2 + 2}
            cy={currentSize.height * 0.12}
            r="0.8"
            fill="#FEF3C7"
            opacity="0.8"
          />
        </g>

        {/* Center Decorative Candle */}
        <g>
          {/* Candle body */}
          <rect
            x={currentSize.width * 0.45}
            y={currentSize.height * 0.2}
            width={currentSize.candleWidth + 1}
            height={currentSize.candleHeight + 2}
            fill="#B45309"
            stroke="#92400E"
            strokeWidth="0.3"
            rx="1"
          />
          {/* Decorative dots pattern */}
          <circle
            cx={currentSize.width * 0.47}
            cy={currentSize.height * 0.3}
            r="0.5"
            fill="#F59E0B"
          />
          <circle
            cx={currentSize.width * 0.53}
            cy={currentSize.height * 0.35}
            r="0.5"
            fill="#F59E0B"
          />
          <circle
            cx={currentSize.width * 0.47}
            cy={currentSize.height * 0.4}
            r="0.5"
            fill="#F59E0B"
          />
          {/* Wick */}
          <line
            x1={currentSize.width * 0.45 + (currentSize.candleWidth + 1) / 2}
            y1={currentSize.height * 0.2}
            x2={currentSize.width * 0.45 + (currentSize.candleWidth + 1) / 2}
            y2={currentSize.height * 0.15}
            stroke="#92400E"
            strokeWidth="0.5"
          />
          {/* Larger flame */}
          <ellipse
            cx={currentSize.width * 0.45 + (currentSize.candleWidth + 1) / 2}
            cy={currentSize.height * 0.1}
            rx={currentSize.flameSize / 1.5}
            ry={currentSize.flameSize / 1.2}
            fill="#FCD34D"
            opacity="0.9"
          />
          <ellipse
            cx={currentSize.width * 0.45 + (currentSize.candleWidth + 1) / 2}
            cy={currentSize.height * 0.1}
            rx={currentSize.flameSize / 3}
            ry={currentSize.flameSize / 2}
            fill="#F59E0B"
          />
          {/* Sparkle effects */}
          <circle
            cx={currentSize.width * 0.45 + (currentSize.candleWidth + 1) / 2 - 2}
            cy={currentSize.height * 0.08}
            r="0.6"
            fill="#FEF3C7"
            opacity="0.8"
          />
          <circle
            cx={currentSize.width * 0.45 + (currentSize.candleWidth + 1) / 2 + 2}
            cy={currentSize.height * 0.12}
            r="0.6"
            fill="#FEF3C7"
            opacity="0.8"
          />
        </g>

        {/* Right Artistic Candle */}
        <g>
          {/* Candle body */}
          <rect
            x={currentSize.width * 0.7}
            y={currentSize.height * 0.25}
            width={currentSize.candleWidth}
            height={currentSize.candleHeight}
            fill="#D97706"
            stroke="#92400E"
            strokeWidth="0.3"
            rx="1"
          />
          {/* Decorative zigzag pattern */}
          <path
            d={`M ${currentSize.width * 0.72} ${currentSize.height * 0.3} L ${currentSize.width * 0.74} ${currentSize.height * 0.32} L ${currentSize.width * 0.76} ${currentSize.height * 0.3} L ${currentSize.width * 0.78} ${currentSize.height * 0.32}`}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="0.4"
          />
          {/* Wick */}
          <line
            x1={currentSize.width * 0.7 + currentSize.candleWidth / 2}
            y1={currentSize.height * 0.25}
            x2={currentSize.width * 0.7 + currentSize.candleWidth / 2}
            y2={currentSize.height * 0.2}
            stroke="#92400E"
            strokeWidth="0.5"
          />
          {/* Flame */}
          <ellipse
            cx={currentSize.width * 0.7 + currentSize.candleWidth / 2}
            cy={currentSize.height * 0.15}
            rx={currentSize.flameSize / 2}
            ry={currentSize.flameSize / 1.5}
            fill="#FCD34D"
            opacity="0.9"
          />
          <ellipse
            cx={currentSize.width * 0.7 + currentSize.candleWidth / 2}
            cy={currentSize.height * 0.15}
            rx={currentSize.flameSize / 4}
            ry={currentSize.flameSize / 2.5}
            fill="#F59E0B"
          />
          {/* Sparkle effect */}
          <circle
            cx={currentSize.width * 0.7 + currentSize.candleWidth / 2 - 2}
            cy={currentSize.height * 0.12}
            r="0.8"
            fill="#FEF3C7"
            opacity="0.8"
          />
        </g>

        {/* Decorative elements around candles */}
        {/* Small hearts for baby shower theme */}
        <g opacity="0.6">
          <path
            d={`M ${currentSize.width * 0.1} ${currentSize.height * 0.2} C ${currentSize.width * 0.08} ${currentSize.height * 0.18}, ${currentSize.width * 0.06} ${currentSize.height * 0.2}, ${currentSize.width * 0.06} ${currentSize.height * 0.22} C ${currentSize.width * 0.06} ${currentSize.height * 0.24}, ${currentSize.width * 0.08} ${currentSize.height * 0.26}, ${currentSize.width * 0.1} ${currentSize.height * 0.24} C ${currentSize.width * 0.12} ${currentSize.height * 0.26}, ${currentSize.width * 0.14} ${currentSize.height * 0.24}, ${currentSize.width * 0.14} ${currentSize.height * 0.22} C ${currentSize.width * 0.14} ${currentSize.height * 0.2}, ${currentSize.width * 0.12} ${currentSize.height * 0.18}, ${currentSize.width * 0.1} ${currentSize.height * 0.2} Z`}
            fill="#F87171"
          />
          <path
            d={`M ${currentSize.width * 0.85} ${currentSize.height * 0.2} C ${currentSize.width * 0.83} ${currentSize.height * 0.18}, ${currentSize.width * 0.81} ${currentSize.height * 0.2}, ${currentSize.width * 0.81} ${currentSize.height * 0.22} C ${currentSize.width * 0.81} ${currentSize.height * 0.24}, ${currentSize.width * 0.83} ${currentSize.height * 0.26}, ${currentSize.width * 0.85} ${currentSize.height * 0.24} C ${currentSize.width * 0.87} ${currentSize.height * 0.26}, ${currentSize.width * 0.89} ${currentSize.height * 0.24}, ${currentSize.width * 0.89} ${currentSize.height * 0.22} C ${currentSize.width * 0.89} ${currentSize.height * 0.2}, ${currentSize.width * 0.87} ${currentSize.height * 0.18}, ${currentSize.width * 0.85} ${currentSize.height * 0.2} Z`}
            fill="#F87171"
          />
        </g>
      </svg>
      
      {/* Business name */}
      <div className="text-center">
        <div className="text-amber-800 dark:text-amber-600 font-bold text-xs sm:text-sm tracking-wide">
          TWINE CANDLES
        </div>
        <div className="text-amber-600 dark:text-amber-500 text-xs font-medium tracking-wider">
          HANDMADE FOR CELEBRATIONS
        </div>
      </div>
    </div>
  );
};

export default CustomCandlesLogo;

